/*
 * Copyright (c) 2026 Centrica.
 *
 */

/**
 * Claves que dicen en que producto esta trabajando el usuario: su id, su codigo, la ruta del
 * modulo y la ficha del modulo elegido.
 */
export const CLAVES_CONTEXTO_PRODUCTO = [
  'selectedProductId',
  'selectedProductCode',
  'selectedRoute',
  'selectedModule',
] as const;

/** Una de las claves del producto activo. */
export type ClaveContextoProducto = (typeof CLAVES_CONTEXTO_PRODUCTO)[number];

/**
 * Lee el producto activo DE ESTA PESTAÑA.
 *
 * <p>El producto vivia solo en `localStorage`, que comparten todas las pestañas del navegador: la
 * ultima que elegia producto le cambiaba a las demas el menu y el buscador (Mantis #455). Ahora la
 * fuente es `sessionStorage`, que es de cada pestaña. `localStorage` solo da el valor inicial: una
 * pestaña nueva arranca con el ultimo producto elegido y, desde ahi, lleva el suyo.</p>
 *
 * @param clave la clave del producto activo.
 * @returns el valor de la pestaña, el heredado al abrirla o `null` si no hay producto.
 */
export function leerContextoDeProducto(clave: ClaveContextoProducto): string | null {
  const propio = sessionStorage.getItem(clave);
  if (propio !== null) {
    return propio;
  }
  const heredado = localStorage.getItem(clave);
  if (heredado !== null) {
    sessionStorage.setItem(clave, heredado);
  }
  return heredado;
}

/**
 * Fija el producto activo en esta pestaña y lo deja como valor inicial de las que se abran despues.
 *
 * @param clave la clave del producto activo.
 * @param valor su valor.
 */
export function guardarContextoDeProducto(clave: ClaveContextoProducto, valor: string): void {
  sessionStorage.setItem(clave, valor);
  localStorage.setItem(clave, valor);
}

/**
 * Quita la clave en esta pestaña y como valor inicial. Las demas pestañas conservan el suyo hasta
 * que cierren sesion: el producto y la sesion siguen muriendo juntos.
 *
 * @param clave la clave del producto activo.
 */
export function quitarContextoDeProducto(clave: ClaveContextoProducto): void {
  sessionStorage.removeItem(clave);
  localStorage.removeItem(clave);
}
